import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import PartnerInvitationConfirmation from '@/components/PartnerInvitationConfirmation';
import { toast } from 'sonner';

export default function InvitationPage() {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [event, setEvent] = useState(null);
  const [inviterProfile, setInviterProfile] = useState(null);
  const [inviterRegistration, setInviterRegistration] = useState(null);

  useEffect(() => {
    fetchInvitationDetails();
  }, [invitationId]);

  const fetchInvitationDetails = async () => {
    if (!invitationId) return;

    try {
      setLoading(true);

      // Get invitation details
      const { data: invitationData, error: invitationError } = await supabase
        .from('partner_invitations')
        .select('*')
        .eq('id', invitationId)
        .single();

      if (invitationError) throw invitationError;

      if (invitationData.status !== 'pending') {
        toast.error('Esta invitación ya no está disponible');
        navigate('/');
        return;
      }

      setInvitation(invitationData);

      // Get event details
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', invitationData.event_id)
        .single();

      if (eventError) throw eventError;
      setEvent(eventData);

      // Get inviter profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('full_name, avatar_url')
        .eq('user_id', invitationData.inviter_id)
        .single();

      if (profileError) throw profileError;
      setInviterProfile(profileData);

      // Get inviter's registration data to show correct category/subcategory
      const { data: registrationData, error: registrationError } = await supabase
        .from('registrations')
        .select('category, subcategory, modality')
        .eq('event_id', invitationData.event_id)
        .eq('user_id', invitationData.inviter_id)
        // El invitador puede tener varias inscripciones (una por categoría/subcategoría);
        // .single() reventaba y rompía la invitación.
        .order('registered_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (registrationError) {
        console.warn('Could not fetch inviter registration:', registrationError);
      } else {
        setInviterRegistration(registrationData);
      }

    } catch (error) {
      console.error('Error fetching invitation:', error);
      toast.error('Error al cargar la invitación');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Payment logic is now handled in PartnerInvitationConfirmation component

  const handleReject = async () => {
    try {
      // Update invitation status
      const { error: updateError } = await supabase
        .from('partner_invitations')
        .update({ status: 'rejected' })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      toast.success('Invitación rechazada');
      navigate('/');
      
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      toast.error('Error al rechazar la invitación');
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!invitation || !event || !inviterProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Invitación no encontrada</p>
      </div>
    );
  }

  const invitationData = {
    partnerName: inviterProfile.full_name || 'Usuario',
    eventId: event.id,
    eventTitle: event.title,
    partnerAvatar: inviterProfile.avatar_url,
    event: event,
    invitation: invitation,
    inviterRegistration: inviterRegistration // Include the inviter's registration data
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <PartnerInvitationConfirmation
        invitationData={invitationData}
        onReject={handleReject}
        onBack={handleBack}
        invitationId={invitationId}
      />
    </div>
  );
}